<template>
  <v-container
    max-width="1024px"
    :class="!isMobile ? 'addBottomSpacingDocument' : ''"
    class="pl-0 pr-0"
  >
    <AlertsComponent class="fixed-top-alert" :context="'documents'" />

    <!-- Header -->
    <v-row class="main-body pt-2 pb-4" align-content="center">
      <v-col cols="auto" align-self="center">
        <v-icon size="34" color="#b4c9db" :icon="mdiFolderOutline" />
      </v-col>
      <v-col cols="auto">
        <h1 class="text-weight-regular text-white" role="heading" aria-level="1">
          {{ $t('L_documents') }}
        </h1>
      </v-col>
    </v-row>

    <!-- Desktop -->
    <v-card v-if="!isMobile" class="docs-card">
      <template #title>
        <v-row>
          <!-- Left side content -->
          <v-col cols="3" class="menu_divider pl-0 pr-0 pt-0 pb-0">
            <v-list v-model="activeTile" class="ma-0">
              <v-list-item
                v-for="(item, i) in documentCategories"
                :key="i"
                @click="navigateToDocumentList(item.routeName)"
                :class="activeTile === i ? 'tile_active' : 'tile_inactive'"
              >
                <div
                  :class="activeTile === i ? 'tile_text_active' : 'tile_text_inactive'"
                  :style="{ color: '#02579e' }"
                  id="categoryTitle"
                >
                  {{ item.title }}
                </div>
              </v-list-item>
            </v-list>
          </v-col>
          <!-- Right side content -->
          <v-col cols="9" class="right_container pl-0 pr-0 pt-0 pb-0">
            <router-view />
          </v-col>
        </v-row>
      </template>
      <!-- Loading Indicator -->
      <template #text v-if="loading">
        <v-row class="pa-4">
          <v-col cols="12">
            <LoadingIndicator :loading="!documentsLoaded" class="mb-16" />
          </v-col>
        </v-row>
      </template>
    </v-card>

    <!-- Mobile -->
    <v-card v-else class="mb-4">
      <v-card>
        <v-list lines="two">
          <template v-for="(item, index) in documentCategories" :key="item.title">
            <v-list-item avatar ripple @click="navigateToDocumentList(item.routeName)">
              <v-list-item-title id="category-title-mobile">
                {{ item.title }}
              </v-list-item-title>
              <template v-slot:append>
                <v-icon color="primary" size="32" :icon="mdiChevronRight" />
              </template>
            </v-list-item>
            <v-divider v-if="index + 1 < documentCategories.length" :key="index" />
          </template>
        </v-list>
      </v-card>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import { mapActions, mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { defineComponent, type ComponentOptions } from 'vue';
import AlertsComponent from '@/components/AlertsComponent.vue';
import { COUNTRIES } from '@/shared/models/constants.models';
import type { IPolicyStorePolicy } from '../policy/store/policyStore.model';
import { mdiChevronRight, mdiFolderOutline } from '@mdi/js';
import { RouteNames } from '@/router/routeNames';

const componentOptions: ComponentOptions = {
  name: 'DocumentsPageDesktop',
  components: {
    LoadingIndicator,
    AlertsComponent,
  },
  data: () => ({
    documentsLoaded: true,
    loading: true,
    // activeTile: 0,
    mdiFolderOutline,
    mdiChevronRight,
  }),
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
      investmentStatements: 'investmentStatements',
    }),
    ...mapState(usePolicyStore, ['policies']),

    ...mapState(useCustomerStore, ['customerProfile', 'investments', 'countryOfOrigin']),
    isMobile() {
      return this.$vuetify.display.xs;
    },
    isCanadian() {
      return this.countryOfOrigin === COUNTRIES.CANADA || this.locale?.indexOf('_CA') > 0;
    },
    hasPolicyDocuments() {
      return this.policies.filter((p: IPolicyStorePolicy) => p.active || p.pending).length > 0;
    },
    hasInvestments() {
      return this.investments?.summary?.totalValue > 0;
    },
    documentCategoriesTitles() {
      return {
        PolicyDocuments: this.$t('L_policy_documents'),
        NoticesAndConfirmations: this.$t('L_notices_and_confirmations'),
        InvestmentStatements: this.$t('L_investment_statements'),
      };
    },
    documentCategories(): Array<{
      title: string;
      routeName: RouteNames;
    }> {
      // see http://confluence.primerica.com/display/PD/Documents++-+Consolidated+View+of+All+Documents#DocumentsConsolidatedViewofAllDocuments-DocumentPage-Categories
      const c = [
        {
          title: this.documentCategoriesTitles.NoticesAndConfirmations, // 'Notices and Confirmations',
          routeName: RouteNames.NOTICES_AND_CONFIRMATIONS,
        },
        {
          title: this.documentCategoriesTitles.PolicyDocuments, // 'Policy Documents',
          routeName: RouteNames.POLICY_DOCUMENTS,
        },
      ];
      if (this.isCanadian) {
        c.push({
          title: this.documentCategoriesTitles.InvestmentStatements, // 'Investment Statements',
          routeName: RouteNames.INVESTMENT_STATEMENTS,
        });
      }
      return c;
    },
    activeTile() {
      return this.selectedDocumentCategoryIndex(this.$route.name as string) || 0;
    },
  },
  async created() {
    await this.fetchDocuments();
    this.loading = false;
    this.documentsLoaded = true;

    const isDocCategoryRoute = [
      RouteNames.NOTICES_AND_CONFIRMATIONS,
      RouteNames.POLICY_DOCUMENTS,
      RouteNames.INVESTMENT_STATEMENTS,
    ].includes(this.$route.name);

    if (this.isMobile && isDocCategoryRoute) {
      // redirect to mobile view;
      this.$router.push({
        name: RouteNames.DOCUMENT_LIST_MOBILE,
        params: { routeName: this.$route.name as string },
      });
    } else if (!this.isMobile && this.$route.name === RouteNames.DOCUMENT_LIST_MOBILE) {
      // redirect to desktop view;
      this.$router.push({
        name: (this.$route.params.routeName as string) || RouteNames.NOTICES_AND_CONFIRMATIONS,
      });
    }
  },
  watch: {
    isMobile(newVal, oldVal) {
      if (
        newVal !== oldVal &&
        newVal &&
        [
          RouteNames.NOTICES_AND_CONFIRMATIONS,
          RouteNames.POLICY_DOCUMENTS,
          RouteNames.INVESTMENT_STATEMENTS,
        ].includes(this.$route.name)
      ) {
        // redirect to mobile view;
        this.$router.push({
          name: RouteNames.DOCUMENT_LIST_MOBILE,
          params: { routeName: this.$route.name as string },
        });
      }
    },
  },
  methods: {
    ...mapActions(useGlobalStore, ['getDocuments']),
    ...mapActions(useCustomerStore, ['getInvestments']),
    async fetchDocuments() {
      await this.getDocuments().catch(() => {
        this.globalStore.addError({
          error: {
            key: 'L_documents_not_available_error',
            message: this.$t('L_documents_not_available_error'),
          },
          context: 'documents',
        });
      });
    },
    navigateToDocumentList(routeName: string) {
      if (this.isMobile) {
        if (
          (routeName as RouteNames) === RouteNames.DOCUMENTS &&
          this.$route.name !== RouteNames.DOCUMENT_LIST_MOBILE
        ) {
          this.$router.push({
            name: RouteNames.DOCUMENT_LIST_MOBILE,
            params: { routeName: RouteNames.NOTICES_AND_CONFIRMATIONS },
          }); // default
        }
        this.$router.push({ name: RouteNames.DOCUMENT_LIST_MOBILE, params: { routeName } });
      } else if (this.$route.name !== routeName) {
        // if not already in the page
        if ((routeName as RouteNames) === RouteNames.DOCUMENTS) {
          this.$router.push({ name: RouteNames.NOTICES_AND_CONFIRMATIONS }); // default
        }
        this.$router.push({ name: routeName });
      }
    },
    selectedDocumentCategoryIndex(routeName: string) {
      return (
        this.documentCategories.findIndex(
          (val: { title: string; routeName: RouteNames }) =>
            val.routeName === (routeName as RouteNames)
        ) || 0
      );
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style scoped lang="scss">
.addBottomSpacingDocument {
  margin-bottom: 120px;
}
.v-list.v-list--two-line.theme--light {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}

.link {
  font-weight: 600;
  color: #0c6dca;
  cursor: pointer;
}

.myNotifications {
  padding-left: 16px;
  padding-right: 16px;
}

.bgAllOfYourAvailable {
  background: #f9f9f9;
}

.contactYourRepButtonWidth {
  width: 264px;
  max-width: 265px;
  min-width: 264px;
}

.picture,
.agentImage,
.icon {
  width: 55px;
  height: 55px;
  margin: 0 auto;
  border-radius: 50%;
}

.rep {
  padding: 15px 0;
  background: #eee;
}

.button-wrapper .v-btn {
  width: 85%;
  margin: 0 auto;
  background-color: #063562 !important;
}

.backgroundColorBlue {
  background-color: #f5f7fa;
}

.backgroundColorWhite {
  background-color: #ffffff;
}

.borderLeftWall {
  border-left: 1px solid #ccc;
}

.paddingForNoDismiss {
  padding-right: 32px;
}

#you-have-no-available {
  line-height: 1.5;
  text-align: center;
  color: #333;
}

#documents-folder-img {
  margin-top: 18px;
  margin-bottom: 18px;
}

.right_container {
  min-height: 800px;
}

.menu_divider {
  border-right: 1px solid #ccc !important;
}

.tile_active,
.tile_active:hover {
  background-color: #e7edf3;
  border-right: 5px solid #3399ff !important;
  padding-top: 16px;
  padding-bottom: 16px;
}

.tile_inactive,
.tile_inactive:hover {
  background-color: #ffffff !important;
  padding-top: 16px;
  padding-bottom: 16px;
}

.tile_text_active {
  font-weight: bold;
  white-space: normal;
  font-size: 14px;
}

.tile_text_inactive {
  white-space: normal;
  font-size: 14px;
}

#category-title-mobile {
  font-size: 16px;
}

:deep(.v-theme--primerica.v-data-table .v-table__wrapper > TABLE > TBODY > TR > td:nth-child(4)) {
  text-align: center;
}

:deep(.v-table__wrapper th:nth-child(4) .v-data-table-header__content) {
  justify-content: center;
}

.docs-card :deep(.v-card-item) {
  padding-top: 0 !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.docs-card :deep(.v-list-item) {
  padding-left: 18px !important;
  padding-right: 4px !important;
}
</style>
